import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
} from '@mui/material';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { Fragment, memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormTreeSelectField from 'components/ui/form/FormTreeSelectField';
import FormSimpleSelectField from 'components/ui/form/FormSimpleSelectField';
import FormSelectField from 'components/ui/form/FormSelectField';
import useFormSubmit from 'hooks/useFormSubmit';
import { useQuery } from 'react-query';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

const validationSchema = yup.object({
  crm_block_item_id: yup
    .string()
    .required('settings.targets.validation.blockId'),
});

const FbAddEditModal = (props) => {
  const { open, setOpen, refetch = () => {}, data } = props;
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [step, setStep] = useState(1);
  const [hasError, setHasError] = useState(false);
  const { submit, isSubmitting } = useFormSubmit();

  const formik = useFormik({
    initialValues: {
      page_id: '',
      form_id: '',

      crm_block_item_id: '',
      crm_block_id: '',
    },
    validationSchema: validationSchema,

    onSubmit: async (values) => {
      submit(
        { type: 'post', contentType: 'simple' },
        values,
        '/admin/facebook/mapping',
        values.client_id,
        null,
        true,
        handleFinishRequest,
      );
    },
  });

  const { data: forms } = useQuery({
    queryKey: ['admin/facebook/form/', formik.values.form_id],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `admin/facebook/form/${formik.values.form_id}`,
      );
      return response.data.data;
    },
    onSuccess: (res) => {
      if (res?.length) {
        res?.forEach((f, i) =>
          formik.setFieldValue(`${i}`, {
            id: f?.id,
            field_id: f?.field_id,
            is_phone: f?.is_phone,
          }),
        );
      }
    },
    enabled: !hasError && !!formik.values.form_id,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  const { data: fields } = useQuery({
    queryKey: ['/crm/crm-settings/fields'],
    queryFn: async function () {
      const response = await axiosPrivate.get('/crm/crm-settings/fields');
      return response.data.data;
    },
    enabled: !hasError,

    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  const handleFinishRequest = () => {
    refetch();
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
    setStep(1);
  };

  const fieldOptions = useMemo(() => {
    const list = fields?.map((f) => ({ id: f?.id, name: f?.name }));
    list?.unshift(
      { id: 'is_phone', name: 'Telefon raqam' },
      { id: 'is_contact_name', name: 'Kontakt nomi' },
    );
    return list;
  }, [fields]);

  const changeFn = (id, i) => {
    const v = formik?.values?.[`${i}`];
    if (id == 'is_phone') {
      formik?.setFieldValue(`${i}`, {
        ...v,
        is_phone: 1,
        is_contact_name: 0,
        field_id: null,
      });
      return;
    }
    if (id == 'is_contact_name') {
      formik?.setFieldValue(`${i}`, {
        ...v,
        is_phone: 0,
        is_contact_name: 1,
        field_id: null,
      });
      return;
    }
    formik?.setFieldValue(`${i}`, {
      ...v,
      is_phone: 0,
      is_contact_name: 0,
      field_id: id,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth='lg'
      PaperProps={{ className: '!base-border !shadow-none' }}
    >
      <DialogTitle id='alert-dialog-title'>
        {data?.id ? (
          <span>
            {t('settings.crm.modal.editOnlinePbx')} Url:pbx12345.onpbx.ru
          </span>
        ) : (
          <span>{t('settings.crm.modal.addFb')}</span>
        )}
        <div className='close-btn-wrapper'>
          <IconButton variant='onlyIcon' color='primary' onClick={handleClose}>
            <i className='bi bi-x' />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent className='!max-w-[600px]'>
        <form onSubmit={formik.handleSubmit}>
          {step == 1 && (
            <Grid
              container
              spacing={{ xs: 2, sm: 3, lg: 3 }}
              rowSpacing={1}
              columns={{ xs: 12, sm: 12, lg: 12 }}
            >
              <Grid item={true} lg={12} sm={12} xs={12}>
                <FormSelectField
                  duration={0}
                  label={t('common.fields.pages')}
                  fieldName='page_id'
                  path='admin/facebook/getpages/2'
                  formik={formik}
                />
              </Grid>
              <Grid item={true} lg={12} sm={12} xs={12}>
                <FormSelectField
                  duration={0}
                  label={t('common.fields.form')}
                  fieldName='form_id'
                  path={`admin/facebook/forms/${formik.values?.page_id}`}
                  formik={formik}
                  pathChangeable
                />
              </Grid>

              <Grid item={true} lg={12} sm={12} xs={12}>
                <Button
                  type='button'
                  variant='contained'
                  onClick={handleClose}
                  className='!mr-1 !bg-[#FF6D6D] hover:!bg-[#eb5757] !shadow-none !rounded-[10px]'
                  color='error'
                >
                  {t('common.button.cancel')}
                </Button>
                <Button
                  variant='contained'
                  color='info'
                  type='button'
                  onClick={() => setStep(2)}
                  disabled={!(formik.values.page_id && formik.values.form_id)}
                >
                  {t('common.button.next')}
                </Button>
              </Grid>
            </Grid>
          )}
          {step == 2 && (
            <Grid
              container
              spacing={{ xs: 2, sm: 3, lg: 3 }}
              rowSpacing={1}
              columns={{ xs: 12, sm: 12, lg: 12 }}
            >
              <Grid item={true} lg={12} sm={12} xs={12}>
                <span className='text-sm'>
                  {t('common.fields.facebookPipe')}
                </span>
                <div className='border border-gray-400 mt-2 p-[5px] rounded-lg [&>*]:!m-0'>
                  <FormTreeSelectField
                    parentFieldName='crm_block_id'
                    childFieldName='crm_block_item_id'
                    label=''
                    childOptionField={'pipes'}
                    formik={formik}
                    parentLabel={'name'}
                    parentValue={'id'}
                    childLabel={'name'}
                    childValue={'id'}
                    variant='standard'
                    path='/crm/block'
                    size='small'
                    onChange={() => {}}
                    progress={false}
                  />
                </div>
              </Grid>
              {forms?.map((form, i) => (
                <Fragment key={i}>
                  <Grid className='border-b' item={true} lg={6} sm={6} xs={6}>
                    <div className='h-full flex items-center'>
                      {form?.facebook_field}
                    </div>
                  </Grid>
                  <Grid className='border-b' item={true} lg={6} sm={6} xs={6}>
                    <FormSimpleSelectField
                      options={fieldOptions}
                      formik={formik}
                      fieldName={`${i}.field_id`}
                      changeFn={(e) => changeFn(e, i)}
                    />
                  </Grid>
                </Fragment>
              ))}

              <Grid item={true} sm={12} xs={12}>
                <FormActionButtons
                  duration={0}
                  isSubmitting={isSubmitting}
                  formType='dialog'
                  setOpen={handleClose}
                  reset={formik.resetForm}
                />
              </Grid>
            </Grid>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default memo(FbAddEditModal);
